import { useState } from 'react';
import _sortBy from 'lodash/sortBy';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import _flatten from 'lodash/flatten';
import _groupBy from 'lodash/groupBy';
import { FormDrawer } from 'common/components/drawer';
import { FormBody } from 'common/components/drawer';
import { FormFooter } from 'common/components/drawer';
import { DrawerState } from 'common/entities/drawer/DrawerTypes';
import { MatricesState, MatrixRuleTypeState } from 'common/entities/crew/MatricesTypes';
import * as matrixActions from 'common/components/crew/components/matrices/store/actions';

import { useForm, useActions } from 'utils/hooks';
import Rules from './rules';
import Wrapper from './components/Wrapper';
import { useEffect } from 'react';

const Body = ({
  config,
  active,
  matrixRuleTypes,
  refetchData,
  drawer,
  isOcean,
  isEditDisabled
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { formState, loadValues, collectValues, resetForm, hasErrors } = useForm(config, {});
  const [createCrewMatrix, updateCrewMatrix] = useActions([
    matrixActions.createCrewMatrix,
    matrixActions.updateCrewMatrix
  ]);
  const isEditMode = _get(active, 'id');

  const sortedRuleTypes = _sortBy(matrixRuleTypes, 'sort_index');

  useEffect(() => {
    const groupRules = _groupBy(active?.rules, 'type.label');
    const subStates = matrixRuleTypes.reduce((acc, curr) => ({ ...acc, [curr?.label]: [] }), {});

    const params = {
      name: active?.name,
      remarks: active?.remarks,
      vessels: active?.vessels,
      ...subStates
    };
    resetForm();
    loadValues({ ...params, ...groupRules });
  }, [
    active?.name,
    active?.remarks,
    active?.rules,
    active?.vessels,
    drawer.isOpen,
    loadValues,
    matrixRuleTypes,
    resetForm
  ]);

  const onSave = async () => {
    try {
      const values = collectValues();
      if (hasErrors) return;
      setIsLoading(true);

      const { name, remarks, vessels, ...rest } = values;
      const rulesData = _flatten(Object.keys(rest)?.map(key => rest[key]))?.map(rule => ({
        rank_id: rule?.rank?.id || null,
        second_rank_id: rule?.second_rank?.id || undefined,
        aggregation_type: rule?.aggregation_type?.label || undefined,
        type_id: rule?.type_id || null,
        threshold: rule?.threshold || null
      }));

      const params = {
        id: active?.id || undefined,
        name: isEditDisabled ? undefined : name,
        remarks: isEditDisabled ? undefined : (remarks ?? ''),
        vessel_ids: isOcean ? undefined : vessels?.map(e => e.id),
        rules: isEditDisabled ? undefined : rulesData
      };

      if (isEditMode) {
        await updateCrewMatrix(params).unwrap();
      } else {
        await createCrewMatrix(params).unwrap();
      }

      drawer.close();
      resetForm();
      refetchData();
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.error(err);
    }
  };

  return (
    <>
      <FormDrawer>
        <FormBody>
          <Wrapper formState={formState} isOcean={isOcean} isEditDisabled={isEditDisabled}>
            {sortedRuleTypes.map((rule, index) => (
              <Rules
                key={index}
                label={rule.label}
                title={rule.name}
                formState={formState}
                isEditDisabled={isEditDisabled}
              />
            ))}
          </Wrapper>
        </FormBody>
      </FormDrawer>

      <FormFooter>
        <Button color="cancel" size="sm" className="px-0 py-1 me-4" onClick={drawer.close}>
          CANCEL
        </Button>

        <Button color="primary" size="sm" className="px-4" disabled={isLoading} onClick={onSave}>
          {isEditMode ? 'SAVE' : 'CREATE'}
        </Button>
      </FormFooter>
    </>
  );
};

export default Body;

Body.propTypes = {
  drawer: DrawerState.isRequired,
  refetchData: PropTypes.func.isRequired,
  active: MatricesState,
  matrixRuleTypes: PropTypes.arrayOf(MatrixRuleTypeState),
  isOcean: PropTypes.bool,
  isEditDisabled: PropTypes.bool
};
